import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import type { AppDefinition } from '@clarix/shared-types';
import { useAuthStore } from '@clarix/auth-store';
import { useMe } from '@/core/api/me.api';
import { useSwitchWorkspace } from '@/core/api/auth.api';
import { getAccessibleApps } from '@/gateway/accessibleApps';
import { useActiveWorkspaceSlug } from '@/gateway/getWorkspaceSlug';
import { isAppLaunchable } from '../appAvailability';
import { bootLog, useBootTrace } from '@/gateway/devtools/bootTrace';

interface Props {
  app: AppDefinition;
  children: ReactNode;
}

/**
 * Guards an app route: enforces workspace enablement AND heals a stale JWT.
 *
 * ENABLEMENT — the app catalog is backend-driven. Once live /me has loaded, an
 * app the active workspace has NOT enabled (or that is not registry-available,
 * e.g. still BETA/COMING_SOON, or that the user's role can't reach) is bounced
 * to the launcher, which re-picks a valid app. This mirrors exactly what the
 * AppSwitcher/launcher show (both read getAccessibleApps), so a disabled app's
 * route can never load its remote. Toggling an app from the admin Apps page
 * therefore controls its route in real time (via /me's 20s heartbeat).
 *
 * STALE JWT — the token's `app_access` claim is a SNAPSHOT from issue time. An
 * app enabled (or a workspace reseeded) AFTER the current token was minted is
 * invisible to the token until the next login. When live /me shows this app
 * ACTIVE (so it IS accessible) — or a different active workspace than the token
 * is bound to — but the in-hand token disagrees, re-issue the token ONCE via
 * switch-workspace (no password; re-resolves app_access), so the app loads on
 * first visit. The one-shot ref + "live says ACTIVE, token says not" condition
 * make this loop-safe, and we never heal for an app we're about to redirect away
 * from.
 */
export function RequireApp({ app, children }: Props): ReactNode {
  const claims = useAuthStore((s) => s.user);
  // Same key as the shell's other /me readers, so one heartbeat serves them all.
  const me = useMe({ activeSlug: useActiveWorkspaceSlug() ?? undefined });
  const switchWs = useSwitchWorkspace();
  const syncedRef = useRef(false);
  useBootTrace(`RequireApp:${app.slug}`, { me: me.status, hasData: !!me.data });

  const meData = me.data;
  const appCode = app.slug.toUpperCase();
  const tokenStatus = claims?.app_access?.[appCode]?.status;
  const liveEntry = meData?.active_workspace_apps?.[appCode];
  const liveStatus = liveEntry?.status;
  const activeSlug = meData?.active_workspace_slug ?? null;
  const tokenSlug = claims?.workspace_slug ?? null;

  // Is this app launchable for the current user in the live workspace? Same
  // source of truth as the AppSwitcher/launcher (workspace-role gate for the
  // admin app; registry_status + enablement for catalog apps).
  const isAccessible = useMemo(() => {
    if (!meData) return true; // don't judge until /me resolves — render meanwhile
    return getAccessibleApps(meData, {
      role: claims?.workspace_role,
      activeSlug: activeSlug ?? undefined,
    }).some((a) => a.slug === app.slug);
  }, [meData, claims?.workspace_role, activeSlug, app.slug]);

  // Stale when the live workspace differs from the token's, or this app is
  // ACTIVE server-side but not ACTIVE in the token.
  const workspaceStale = !!activeSlug && activeSlug !== tokenSlug;
  const appStale = liveStatus === 'ACTIVE' && tokenStatus !== 'ACTIVE';

  useEffect(() => {
    if (!meData || syncedRef.current) return;
    // Never heal a token for an app we're about to bounce away from.
    if (!isAccessible) return;
    if (!activeSlug) return;
    if (!workspaceStale && !appStale) return;
    syncedRef.current = true;
    bootLog('RequireApp token heal → switch-workspace', { workspaceStale, appStale });
    switchWs.mutate({ workspace_slug: activeSlug });
  }, [meData, isAccessible, activeSlug, workspaceStale, appStale, switchWs]);

  // Enablement gate — only once /me has SUCCEEDED (isAccessible defaults true
  // while loading, so we never flash-redirect before the app map is known). We
  // gate on me.isSuccess, not merely cached data, so a failed heartbeat or a
  // transient /me error never bounces the user out of an app they legitimately
  // have open — a genuine de-enable still redirects on the next successful /me.
  if (me.isSuccess && !isAccessible) {
    return <Navigate to="/" replace />;
  }

  if (me.isLoading) return null;

  // The workspace admin app is governed by role, not by app access — never gate it here.
  if (app.slug !== 'workspace') {
    // Blocks a member whose access is PAUSED/SUSPENDED, and any app the App
    // registry marks COMING_SOON/DEPRECATED. A missing entry means /me has not
    // told us about this app at all; left as-is (allowed) rather than widening
    // the guard, since the switcher already never surfaces such an app.
    if (me.isSuccess && liveEntry && !isAppLaunchable(liveEntry)) {
      return <Navigate to="/" replace />;
    }
  }

  return children;
}
